import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import WatchlistEditor from "./WatchlistEditor";

function num(v, d = 2) {
  const n = parseFloat(v ?? 0);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function Strategy() {
  const [state, setState] = useState(null);
  const [signals, setSignals] = useState([]);
  const [strategyPositions, setStrategyPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    const [s, sig, pos] = await Promise.all([
      api.get("/strategy/state"),
      api.get("/strategy/signals?limit=50"),
      api.get("/strategy/positions"),
    ]);
    setState(s.data);
    setSignals(sig.data || []);
    setStrategyPositions(pos.data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const patch = async (update) => {
    const r = await api.patch("/strategy/state", update);
    setState(r.data);
    toast.success("Strategy updated");
  };

  const toggleEnabled = () => patch({ enabled: !state.enabled });

  const runScanNow = async () => {
    setScanning(true);
    try {
      const r = await api.post("/strategy/scan-now");
      toast.success(`Scan complete: ${r.data.count || 0} actions`);
      await load();
    } catch (err) {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  if (loading || !state) {
    return <div className="p-6 mono text-xs text-[#71717A]">LOADING...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="strategy-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">
            {(state.entry_strategy || "macd") === "slingshot"
              ? `SLINGSHOT EMA ${state.sling_fast_ema || 38}·${state.sling_slow_ema || 62} · ${state.bar_timeframe || "1Hour"}`
              : (state.entry_strategy || "macd") === "orderblock"
              ? `ORDER BLOCK · BUY% ${num(state.ob_long_min_buy || 65, 0)}/${num(state.ob_short_max_buy || 40, 0)} · ${state.bar_timeframe || "1Hour"}`
              : `MACD CROSSOVER · ${state.bar_timeframe || "1Hour"} · 12·26·9`}
          </div>
          <h1 className="text-4xl font-black mt-2">Strategy Command Center</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            <span className="gain">LONG</span> on bullish + MACD ≥ <span className="mono text-white">{num(state.long_threshold, 2)}</span> (bracket order).{" "}
            <span className="loss">SHORT</span> on bearish + MACD ≤ <span className="mono text-white">{num(state.short_threshold, 2)}</span>{state.enable_shorts ? "" : " (disabled)"}. TP {num(state.take_profit_pct, 1)}% · SL {num(state.stop_loss_pct, 1)}%.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={runScanNow}
            disabled={scanning || !state.enabled}
            data-testid="scan-now-btn"
            className="border border-[#27272A] hover:border-[#52525B] px-4 py-2 mono text-[10px] tracking-[0.22em] disabled:opacity-40"
          >
            {scanning ? "SCANNING..." : "SCAN NOW →"}
          </button>
          <button
            onClick={toggleEnabled}
            data-testid="toggle-strategy-btn"
            className={`px-6 py-2.5 mono text-xs tracking-[0.22em] font-bold ${
              state.enabled ? "bg-[#FF3B30] text-white" : "bg-[#00E676] text-black"
            }`}
          >
            {state.enabled ? "■ DISARM BOT" : "▶ ARM BOT"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-7 gap-px bg-[#27272A] border border-[#27272A]" data-testid="strategy-stats">
        {[
          { label: "STATUS", value: state.enabled ? "ARMED" : "DISARMED", color: state.enabled ? "gain" : "loss" },
          { label: "TAKE PROFIT", value: `${num(state.take_profit_pct, 1)}%`, color: "gain" },
          { label: "STOP LOSS", value: `${num(state.stop_loss_pct, 1)}%`, color: "loss" },
          {
            label: "POSITION SIZE",
            value: state.sizing_mode === "fixed_qty" ? `${num(state.qty_per_trade, 0)} sh` : `${num(state.allocation_pct, 0)}%`,
          },
          { label: "MAX POS", value: num(state.max_positions, 0) },
          { label: "LONG ≥", value: num(state.long_threshold, 2), color: "gain" },
          { label: "SHORT ≤", value: num(state.short_threshold, 2), color: "loss" },
        ].map((s) => (
          <div key={s.label} className="bg-[#141415] p-4">
            <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">{s.label}</div>
            <div className={`mono text-xl mt-2 ${s.color || ""}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 panel" data-testid="signals-panel">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
            <div className="mono text-[11px] tracking-[0.22em]">SIGNAL FEED · {signals.length}</div>
            <div className="mono text-[10px] text-[#71717A]">
              LAST SCAN {state.last_scan_at ? new Date(state.last_scan_at).toLocaleTimeString() : "—"}
            </div>
          </div>
          {signals.length === 0 ? (
            <div className="p-10 text-center text-[#71717A] mono text-xs">
              NO SIGNALS YET — ARM THE BOT TO BEGIN SCANNING
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#141415]">
                  <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                    <th className="text-left px-4 py-2 font-normal">TIME</th>
                    <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                    <th className="text-left px-4 py-2 font-normal">SIGNAL</th>
                    <th className="text-right px-4 py-2 font-normal">PRICE</th>
                    <th className="text-right px-4 py-2 font-normal">MACD</th>
                    <th className="text-left px-4 py-2 font-normal">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((sig, i) => {
                    const hasErr = sig.bracket_error || sig.safety_sl_error;
                    const unprotected = sig.unprotected === true;
                    return (
                      <React.Fragment key={i}>
                        <tr className={`border-b border-[#27272A] hover:bg-[#1C1C1E] ${unprotected ? "bg-[#FF3B30]/10" : ""}`} data-testid={`signal-row-${i}`}>
                          <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">
                            {new Date(sig.timestamp).toLocaleTimeString()}
                            {sig.bar_time && (
                              <div className="text-[9px] text-[#52525B]" title="Bar time (data timestamp)">
                                bar {new Date(sig.bar_time).toLocaleTimeString()}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 font-bold mono">
                            {sig.symbol}
                            {unprotected && <span className="ml-2 text-[10px] loss" title="No stop-loss attached at broker">⚠ UNPROTECTED</span>}
                          </td>
                          <td className="px-4 py-2 mono text-xs">
                            <span className={sig.crossover === "bullish" ? "gain" : sig.crossover === "bearish" ? "loss" : "text-[#A1A1AA]"}>
                              {sig.crossover ? sig.crossover.toUpperCase() : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right mono">{num(sig.price)}</td>
                          <td className="px-4 py-2 text-right mono text-[#A1A1AA]">{num(sig.macd, 3)}</td>
                          <td className="px-4 py-2 mono text-xs">
                            {sig.action ? (
                              <span className={
                                sig.action === "buy" ? "gain" :
                                sig.action.includes("sell") || sig.action.includes("stop") ? "loss" : "text-white"
                              }>
                                {sig.action.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-[#71717A]">OBSERVE</span>
                            )}
                          </td>
                        </tr>
                        {hasErr && (
                          <tr className="border-b border-[#27272A] bg-[#FF3B30]/5" data-testid={`signal-error-${i}`}>
                            <td colSpan={6} className="px-4 py-2 mono text-[10px] text-[#FF3B30] leading-relaxed">
                              {sig.bracket_error && <div>⚠ BRACKET FAILED: {sig.bracket_error}</div>}
                              {sig.safety_sl_error && <div>⚠ SAFETY-NET SL FAILED: {sig.safety_sl_error}</div>}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="panel" data-testid="config-panel">
            <div className="px-4 py-3 border-b border-[#27272A]">
              <div className="mono text-[11px] tracking-[0.22em]">CONFIGURATION</div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">STOP LOSS (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="50"
                  defaultValue={state.stop_loss_pct}
                  onBlur={(e) => patch({ stop_loss_pct: parseFloat(e.target.value) })}
                  data-testid="config-stoploss-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">TAKE PROFIT (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="100"
                  defaultValue={state.take_profit_pct}
                  onBlur={(e) => patch({ take_profit_pct: parseFloat(e.target.value) })}
                  data-testid="config-takeprofit-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  Longs use Alpaca bracket orders · shorts managed by scanner
                </div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">SIZING MODE</label>
                <div className="mt-1 grid grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
                  {[
                    { v: "pct_cash", label: "% CASH" },
                    { v: "pct_equity", label: "% EQUITY" },
                    { v: "fixed_qty", label: "FIXED QTY" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`sizing-${opt.v}-btn`}
                      onClick={() => patch({ sizing_mode: opt.v })}
                      className={`py-2 mono text-[10px] tracking-[0.18em] ${
                        state.sizing_mode === opt.v
                          ? "bg-white text-black"
                          : "bg-[#141415] text-[#A1A1AA] hover:bg-[#1C1C1E]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  {state.sizing_mode === "pct_cash" && "Cash-only · no margin · qty capped to available cash"}
                  {state.sizing_mode === "pct_equity" && "Uses full equity / Alpaca buying power · margin allowed"}
                  {state.sizing_mode === "fixed_qty" && "Fixed share count per trade"}
                </div>
              </div>

              {(state.sizing_mode === "pct_equity" || state.sizing_mode === "pct_cash") ? (
                <div>
                  <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">
                    ALLOCATION % PER TRADE ({state.sizing_mode === "pct_cash" ? "OF CASH" : "OF EQUITY"})
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="100"
                    defaultValue={state.allocation_pct}
                    onBlur={(e) => patch({ allocation_pct: parseFloat(e.target.value) })}
                    data-testid="config-allocation-input"
                    className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                  />
                  <div className="mono text-[10px] text-[#71717A] mt-1">
                    e.g. 20% × 5 max positions = 100% deployed
                  </div>
                </div>
              ) : (
                <div>
                  <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">QTY PER TRADE (SHARES)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    defaultValue={state.qty_per_trade}
                    onBlur={(e) => patch({ qty_per_trade: parseFloat(e.target.value) })}
                    data-testid="config-qty-input"
                    className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                  />
                </div>
              )}

              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">MAX CONCURRENT POSITIONS</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  defaultValue={state.max_positions}
                  onBlur={(e) => patch({ max_positions: parseInt(e.target.value, 10) })}
                  data-testid="config-max-positions-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  Bot won&apos;t open new trades once this limit is hit
                </div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">ENTRY STRATEGY</label>
                <div className="mt-1 grid grid-cols-3 gap-0 border border-[#27272A]">
                  {[
                    { v: "macd", label: "MACD" },
                    { v: "slingshot", label: "SLINGSHOT" },
                    { v: "orderblock", label: "ORDER BLOCK" },
                  ].map((opt, idx) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => patch({ entry_strategy: opt.v })}
                      data-testid={`config-entry-${opt.v}`}
                      className={`mono text-[10px] tracking-[0.18em] py-2 transition-colors ${
                        (state.entry_strategy || "macd") === opt.v
                          ? "bg-white text-black font-bold"
                          : "bg-[#09090B] text-[#71717A] hover:text-white"
                      } ${idx < 2 ? "border-r border-[#27272A]" : ""}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  {(state.entry_strategy || "macd") === "slingshot"
                    ? `Trend = EMA${state.sling_fast_ema || 38} vs EMA${state.sling_slow_ema || 62}. Buys the pullback: price dips through the fast EMA, then reclaims it. MACD thresholds below are ignored.`
                    : (state.entry_strategy || "macd") === "orderblock"
                    ? `Volume order-block retest: zones form at swing pivots and get a buy/sell volume split. LONG when price retests a zone with buy% ≥ ${num(state.ob_long_min_buy || 65, 0)}, SHORT when buy% ≤ ${num(state.ob_short_max_buy || 40, 0)}. MACD thresholds ignored.`
                    : "Classic MACD 12·26·9 crossover, gated by the thresholds below."}
                </div>
                {(state.entry_strategy || "macd") === "slingshot" && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">FAST EMA</label>
                      <input type="number" step="1" min="5" max="100" defaultValue={state.sling_fast_ema || 38}
                        onBlur={(e) => patch({ sling_fast_ema: parseInt(e.target.value, 10) || 38 })}
                        data-testid="config-sling-fast-input"
                        className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                    </div>
                    <div>
                      <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">SLOW EMA</label>
                      <input type="number" step="1" min="10" max="300" defaultValue={state.sling_slow_ema || 62}
                        onBlur={(e) => patch({ sling_slow_ema: parseInt(e.target.value, 10) || 62 })}
                        data-testid="config-sling-slow-input"
                        className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                    </div>
                  </div>
                )}
                {(state.entry_strategy || "macd") === "orderblock" && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">LONG WHEN BUY% ≥</label>
                      <input type="number" step="1" min="50" max="95" defaultValue={state.ob_long_min_buy || 65}
                        onBlur={(e) => patch({ ob_long_min_buy: parseFloat(e.target.value) || 65 })}
                        data-testid="config-ob-long-input"
                        className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                      <div className="text-[#71717A] text-[10px] mt-1">65 = default · 75 = stricter zones only</div>
                    </div>
                    <div>
                      <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">SHORT WHEN BUY% ≤</label>
                      <input type="number" step="1" min="5" max="50" defaultValue={state.ob_short_max_buy || 40}
                        onBlur={(e) => patch({ ob_short_max_buy: parseFloat(e.target.value) || 40 })}
                        data-testid="config-ob-short-input"
                        className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                      <div className="text-[#71717A] text-[10px] mt-1">40 = default · 30 = stricter zones only</div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">EXIT MODE</label>
                <div className="mt-1 grid grid-cols-2 gap-0 border border-[#27272A]">
                  {[
                    { v: "fixed", label: "FIXED TP / SL" },
                    { v: "trailing", label: "TRAILING STOP" },
                  ].map((opt, idx) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => patch({ exit_mode: opt.v })}
                      data-testid={`config-exit-${opt.v}`}
                      className={`mono text-[10px] tracking-[0.18em] py-2 transition-colors ${
                        (state.exit_mode || "fixed") === opt.v
                          ? "bg-white text-black font-bold"
                          : "bg-[#09090B] text-[#71717A] hover:text-white"
                      } ${idx === 0 ? "border-r border-[#27272A]" : ""}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  {(state.exit_mode || "fixed") === "trailing"
                    ? `No profit cap — an Alpaca-native trailing stop follows the price up by ${num(state.trail_pct || 3, 1)}% and fires in real-time. Losers cut fast, winners ride.`
                    : "Bracket order with fixed take-profit and stop-loss percentages."}
                </div>
                {(state.exit_mode || "fixed") === "trailing" && (
                  <div className="mt-2">
                    <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">TRAIL %</label>
                    <input type="number" step="0.5" min="0.5" max="20" defaultValue={state.trail_pct || 3}
                      onBlur={(e) => patch({ trail_pct: parseFloat(e.target.value) || 3 })}
                      data-testid="config-trail-pct-input"
                      className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-32" />
                    <span className="text-[#71717A] text-[10px] ml-2">3% = default · 1.5% = tight (hourly) · 5% = loose (volatile stocks)</span>
                  </div>
                )}
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">LONG ENTRY · MACD ≥</label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue={state.long_threshold}
                  onBlur={(e) => patch({ long_threshold: parseFloat(e.target.value) })}
                  data-testid="config-long-threshold-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
                <div className="mono text-[10px] text-[#71717A] mt-1">Default 0.20 — filters weak bullish crossovers</div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">SHORT/EXIT · MACD ≤</label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue={state.short_threshold}
                  onBlur={(e) => patch({ short_threshold: parseFloat(e.target.value) })}
                  data-testid="config-short-threshold-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
                <div className="mono text-[10px] text-[#71717A] mt-1">Default −0.20 — filters weak bearish exits</div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">BAR TIMEFRAME</label>
                <select
                  value={state.bar_timeframe || "1Hour"}
                  onChange={(e) => patch({ bar_timeframe: e.target.value })}
                  data-testid="config-bar-timeframe-select"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                >
                  <option value="5Min">5 Min</option>
                  <option value="15Min">15 Min</option>
                  <option value="30Min">30 Min</option>
                  <option value="1Hour">1 Hour</option>
                  <option value="1Day">1 Day</option>
                </select>
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  MACD chart interval for scanner + backtest. Match with scan interval for tight signals.
                </div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">RE-ENTRY COOLDOWN · MINUTES</label>
                <input
                  type="number"
                  step="15"
                  min="0"
                  defaultValue={state.cooldown_after_exit_min ?? 60}
                  onBlur={(e) => patch({ cooldown_after_exit_min: parseInt(e.target.value, 10) || 0 })}
                  data-testid="config-cooldown-input"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  After ANY exit (SL/TP/manual), skip new entries on that symbol for this many minutes. Prevents whipsaw re-buys. Set 0 to disable.
                </div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">SHORT POSITIONS</label>
                <button
                  type="button"
                  onClick={() => patch({ enable_shorts: !state.enable_shorts })}
                  data-testid="toggle-shorts-btn"
                  className={`mt-1 w-full py-2 mono text-[11px] tracking-[0.22em] font-bold border ${
                    state.enable_shorts
                      ? "bg-[#FF3B30] text-white border-[#FF3B30]"
                      : "bg-[#141415] text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]"
                  }`}
                >
                  {state.enable_shorts ? "■ SHORTS ENABLED — CLICK TO DISABLE" : "▶ SHORTS DISABLED — CLICK TO ENABLE"}
                </button>
                <div className="mono text-[10px] text-[#71717A] mt-1">
                  Requires short-sell permission on your Alpaca account
                </div>
              </div>
              <div>
                <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">WATCHLIST · {state.watchlist?.length || 0} SYMBOLS</label>
                <div className="mt-2 max-h-40 overflow-y-auto border border-[#27272A] p-2 mono text-xs flex flex-wrap gap-1.5">
                  {(state.watchlist || []).map((s) => (
                    <span key={s} className="px-1.5 py-0.5 bg-[#1C1C1E] border border-[#27272A]">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="panel" data-testid="filters-panel">
            <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em]">
              FILTERS · DISCRETIONARY EDGE
            </div>
            <div className="p-4 grid grid-cols-1 gap-3">
              {/* Trend Filter */}
              <div className="border border-[#27272A] p-3">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">📈 TREND FILTER (EMA)</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_trend_filter: !state.enable_trend_filter })}
                    data-testid="toggle-trend-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_trend_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_trend_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2">
                  Long only when price &gt; {state.trend_ema_period}-EMA, short only when below. Cuts whipsaws dramatically.
                </div>
              </div>

              {/* Volume Filter */}
              <div className="border border-[#27272A] p-3">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">🌊 VOLUME ≥ {num(state.volume_multiplier, 1)}× AVG</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_volume_filter: !state.enable_volume_filter })}
                    data-testid="toggle-volume-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_volume_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_volume_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2">
                  Requires entry-bar volume above the {state.volume_avg_period}-bar moving average.
                </div>
              </div>

              {/* MTF Filter */}
              <div className="border border-[#27272A] p-3">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">⏱ DAILY MACD AGREES</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_mtf_filter: !state.enable_mtf_filter })}
                    data-testid="toggle-mtf-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_mtf_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_mtf_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2">
                  Only take 1h longs when daily MACD &gt; 0 (and reverse for shorts). Strong trend alignment.
                </div>
              </div>

              {/* SPY Trend Filter — NEW Feb 2026 */}
              <div className="border border-[#FFD60A] p-3 bg-[#FFD60A]/5" data-testid="spy-trend-filter-section">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#FFD60A]">📊 SPY MARKET TREND · NEW</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_spy_trend_filter: !state.enable_spy_trend_filter })}
                    data-testid="toggle-spy-trend-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_spy_trend_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_spy_trend_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2 leading-relaxed">
                  <span className="text-white">Don&apos;t fight the market.</span> Only takes LONG signals when SPY is above its {state.spy_trend_ema_period || 50}-EMA,
                  and SHORT signals when SPY is below. Single biggest alpha filter for MACD systems —
                  research-backed +5 to +10% win-rate lift.
                </div>
                {state.enable_spy_trend_filter && (
                  <div className="mt-2">
                    <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">SPY EMA PERIOD</label>
                    <input type="number" step="1" min="10" max="200" defaultValue={state.spy_trend_ema_period || 50}
                      onBlur={(e) => patch({ spy_trend_ema_period: parseInt(e.target.value, 10) })}
                      data-testid="config-spy-trend-period-input"
                      className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-24" />
                    <span className="text-[#71717A] text-[10px] ml-2">50 = swing trader · 200 = long-term trend</span>
                  </div>
                )}
              </div>

              {/* RSI Filter — NEW Feb 2026 */}
              <div className="border border-[#FFD60A] p-3 bg-[#FFD60A]/5" data-testid="rsi-filter-section">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#FFD60A]">🌡 RSI CONFIRM · NEW</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_rsi_filter: !state.enable_rsi_filter })}
                    data-testid="toggle-rsi-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_rsi_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_rsi_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2 leading-relaxed">
                  {(state.rsi_mode || "avoid_extremes") === "momentum" ? (
                    <>
                      <span className="text-white">Momentum mode.</span> ONLY take LONG signals when RSI &gt; {num(state.rsi_long_max || 70, 0)} (strong trend),
                      ONLY take SHORT signals when RSI &lt; {num(state.rsi_short_min || 30, 0)} (strong weakness).
                      Rides strength instead of avoiding it — expect fewer but more directional entries.
                    </>
                  ) : (
                    <>
                      <span className="text-white">Avoid extremes.</span> Skip LONG signals when RSI &gt; {num(state.rsi_long_max || 70, 0)} (overbought tops),
                      skip SHORT signals when RSI &lt; {num(state.rsi_short_min || 30, 0)} (oversold bottoms).
                      Filters MACD false signals at price extremes — typical +3 to +7% win-rate lift.
                    </>
                  )}
                </div>
                {state.enable_rsi_filter && (
                  <>
                    <div className="mt-3">
                      <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">RSI MODE</label>
                      <div className="grid grid-cols-2 gap-0 border border-[#27272A]">
                        {[
                          { v: "avoid_extremes", label: "AVOID EXTREMES" },
                          { v: "momentum", label: "MOMENTUM" },
                        ].map((opt, idx) => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => patch({ rsi_mode: opt.v })}
                            data-testid={`config-rsi-mode-${opt.v}`}
                            className={`mono text-[10px] tracking-[0.18em] py-2 transition-colors ${
                              (state.rsi_mode || "avoid_extremes") === opt.v
                                ? "bg-[#FFD60A] text-black font-bold"
                                : "bg-[#09090B] text-[#71717A] hover:text-white"
                            } ${idx === 0 ? "border-r border-[#27272A]" : ""}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">
                          {(state.rsi_mode || "avoid_extremes") === "momentum" ? "BUY WHEN RSI >" : "RSI LONG MAX"}
                        </label>
                        <input type="number" step="1" min="50" max="90" defaultValue={state.rsi_long_max || 70}
                          onBlur={(e) => patch({ rsi_long_max: parseFloat(e.target.value) })}
                          data-testid="config-rsi-long-max-input"
                          className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                        <div className="text-[#71717A] text-[10px] mt-1">
                          {(state.rsi_mode || "avoid_extremes") === "momentum" ? "70 = default · 60 = looser · 75 = stricter" : "70 = default · 65 = stricter · 80 = looser"}
                        </div>
                      </div>
                      <div>
                        <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">
                          {(state.rsi_mode || "avoid_extremes") === "momentum" ? "SHORT WHEN RSI <" : "RSI SHORT MIN"}
                        </label>
                        <input type="number" step="1" min="10" max="50" defaultValue={state.rsi_short_min || 30}
                          onBlur={(e) => patch({ rsi_short_min: parseFloat(e.target.value) })}
                          data-testid="config-rsi-short-min-input"
                          className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                        <div className="text-[#71717A] text-[10px] mt-1">
                          {(state.rsi_mode || "avoid_extremes") === "momentum" ? "30 = default · 40 = looser · 25 = stricter" : "30 = default · 35 = stricter · 20 = looser"}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Histogram Slope Filter — NEW Feb 2026 */}
              <div className="border border-[#FFD60A] p-3 bg-[#FFD60A]/5" data-testid="hist-slope-filter-section">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#FFD60A]">📐 MACD MOMENTUM EXPANDING · NEW</div>
                  <button
                    type="button"
                    onClick={() => patch({ enable_hist_slope_filter: !state.enable_hist_slope_filter })}
                    data-testid="toggle-hist-slope-filter"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.enable_hist_slope_filter ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.enable_hist_slope_filter ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-2 leading-relaxed">
                  <span className="text-white">Require real momentum.</span> Only enters when MACD histogram is expanding (bar-over-bar) in the
                  signal direction. Filters out weak crossovers in choppy markets where the histogram barely changes direction.
                  Typical +3 to +5% win-rate lift, ~25% fewer trades.
                </div>
              </div>

              {/* ATR Stops */}
              <div className="border border-[#27272A] p-3" data-testid="atr-stops-section">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">🎯 ATR DYNAMIC STOPS</div>
                  <button
                    type="button"
                    onClick={() => patch({ use_atr_stops: !state.use_atr_stops })}
                    data-testid="toggle-atr-stops"
                    className={`px-3 py-1 mono text-[10px] tracking-widest font-bold ${
                      state.use_atr_stops ? "bg-[#00E676] text-black" : "bg-[#27272A] text-[#A1A1AA]"
                    }`}
                  >
                    {state.use_atr_stops ? "ON" : "OFF"}
                  </button>
                </div>

                <div className="text-[#A1A1AA] text-xs mt-2 leading-relaxed">
                  <span className="text-white">What this does:</span> instead of a fixed % stop on every trade,
                  the bot sizes stops to each stock&apos;s own volatility (ATR = Average True Range).
                  Calm stocks (AAPL) get tight stops, volatile ones (TSLA, COIN) get wider stops —
                  so you stop getting whipsawed by normal noise.
                </div>

                {state.use_atr_stops && (
                  <div className="mt-2 px-2 py-1.5 bg-[#0A0A0A] border border-[#27272A] mono text-[10px] text-[#A1A1AA]" data-testid="atr-preview">
                    Example: stock with $2.50 ATR → SL ≈ <span className="loss">${num(2.5 * (state.atr_mult_sl || 1.5), 2)}</span> below entry,
                    TP ≈ <span className="gain">${num(2.5 * (state.atr_mult_tp || 3.0), 2)}</span> above entry.
                    Reward / risk ≈ <span className="text-white">{num((state.atr_mult_tp || 3.0) / (state.atr_mult_sl || 1.5), 2)}:1</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">
                      ATR × SL MULT
                    </label>
                    <input type="number" step="0.1" min="0.5" max="5" defaultValue={state.atr_mult_sl}
                      onBlur={(e) => patch({ atr_mult_sl: parseFloat(e.target.value) })}
                      data-testid="config-atr-sl-input"
                      className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                    <div className="text-[#71717A] text-[10px] mt-1 leading-snug">
                      Distance from entry to stop, in ATRs.<br/>
                      <span className="text-[#A1A1AA]">1.5 = tight (default) · 2.0 = looser · 1.0 = very tight</span>
                    </div>
                  </div>
                  <div>
                    <label className="mono text-[10px] tracking-[0.18em] text-[#71717A] block mb-1">
                      ATR × TP MULT
                    </label>
                    <input type="number" step="0.1" min="0.5" max="10" defaultValue={state.atr_mult_tp}
                      onBlur={(e) => patch({ atr_mult_tp: parseFloat(e.target.value) })}
                      data-testid="config-atr-tp-input"
                      className="bg-[#09090B] border border-[#27272A] px-2 py-1 mono text-xs w-full" />
                    <div className="text-[#71717A] text-[10px] mt-1 leading-snug">
                      Distance from entry to take-profit, in ATRs.<br/>
                      <span className="text-[#A1A1AA]">3.0 = 2:1 reward (default) · 4.0 = let winners run</span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-[#71717A] text-[10px] leading-snug border-t border-[#27272A] pt-2">
                  <span className="text-[#A1A1AA]">Note:</span> Only applies to <span className="text-white">new</span> positions
                  opened after you turn this on. Existing positions keep their original brackets.
                  Backtest the change first via the Backtest page to compare vs fixed % stops.
                </div>
              </div>
            </div>
          </div>

          <div className="panel" data-testid="bot-positions-panel">
            <div className="px-4 py-3 border-b border-[#27272A]">
              <div className="mono text-[11px] tracking-[0.22em]">BOT POSITIONS · {strategyPositions.filter(p => p.status === "open").length} OPEN</div>
            </div>
            {strategyPositions.length === 0 ? (
              <div className="p-6 text-center text-[#71717A] mono text-xs">NO BOT TRADES YET</div>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {strategyPositions.slice(0, 20).map((p, i) => (
                  <div key={i} className="px-4 py-2 border-b border-[#27272A] flex justify-between items-center" data-testid={`bot-pos-${p.symbol}`}>
                    <div>
                      <div className="font-bold mono text-sm flex items-center gap-2">
                        {p.symbol}
                        <span className={`mono text-[9px] px-1.5 py-0.5 border ${p.side === "short" ? "loss border-[#FF3B30]" : "gain border-[#00E676]"}`}>
                          {(p.side || "long").toUpperCase()}
                        </span>
                      </div>
                      <div className="mono text-[10px] text-[#71717A]">
                        {p.status === "open"
                          ? `TP ${num(p.target_price)} · SL ${num(p.stop_price)}`
                          : `EXIT ${p.exit_reason}`}
                      </div>
                    </div>
                    <div className="text-right mono text-xs">
                      <div>{num(p.entry_price)}</div>
                      <div className="text-[#71717A]">{p.status?.toUpperCase()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Watchlist editor */}
      <WatchlistEditor />
    </div>
  );
}
